class EnumHelper {
    /**
    * Get the constant names and values for a given enum type.
    * @param enumType The enum type.
    */
    public static getNamesAndValues<TEnum extends number>(enumType: Record<number, string>): { name: string, value: TEnum }[] {
        return EnumHelper
            .getNames(enumType)
            .map(name => ({ name: name, value: enumType[name] as TEnum }));
    }

    /**
    * Get the constant names for a given enum type.
    * @param enumType The enum type.
    */
    public static getNames(enumType: Record<number, string>): string[] {
        return Object.values(enumType).filter(p => typeof p === "string") as string[];
    }

    /**
    * Get the values for a given enum type.
    * @param type The enum type.
    */
    public static getValues<TEnum extends number>(enumType: Record<number, string>): TEnum[] {
        return Object.values(enumType).filter(p => typeof p === "number") as TEnum[];
    }

    /**
    * Get if the value, or its name as a string, exists in a given enum type.
    * @param enumType The enum type.
    * @param value The enum constant name or value to look up.
    */
    public static isDefined(enumType: Record<number, string>, value: (number | string)): boolean {
        return Object.values(enumType).any(p => p === value);
    }

    /**
    * Get the constant name in the specified enum type that has the specified value or constant name.
    * @param type The enum type.
    * @param value The enum constant name or value to look up.
    */
    public static getName(enumType: Record<number, string>, value: number | string): string {
        // if it's a number, get the name from the enum lookup
        if (typeof value === "number" || !isNaN(parseInt(value)))
            return enumType[value] ?? `${value}`;

        // otherwise return the string value as-is
        return `${value}`;
    }

    /**
    * Get the value in the specified enum type that has the specified value or constant name.
    * @param type The enum type.
    * @param value The enum constant name or value to look up.
    */
    public static getValue(enumType: Record<number, string>, value: number | string): number {
        if (typeof value === "number")
            return value;

        const parsed = parseInt(value);
        if (!isNaN(parsed))
            return parsed;

        return enumType[value];
    }

    /**
    * Get the for the the specified enum type that has the specified value or constant name.
    * @param enumType The enum type name.
    * @param keyPrefix The vocabulary key prefix for the enum.
    * @param value The enum constant name or value to look up.
    */
    public static getVocab(enumType: Record<number, string>, keyPrefix: string, value: number | string): string {
        const name = EnumHelper.getName(enumType, value);
        const key = keyPrefix + name;
        return cacheStore.HasString(key)
            ? cacheStore.GetString(key)
            : name;
    }
}

export { EnumHelper };
